import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { useCurrentTalentProfile } from "@/shared";
import { useDeleteMainCard } from "@/cards/common/api/taascard.api";
import useThemeStore from "@/shared/stores/useThemeStore";

export interface DeleteCardConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Main-card row id to delete (`/main-card/:id`). String or number accepted. */
  cardId: number | string;
  /** Optional title rendered inside the confirmation copy. */
  cardTitle?: string;
  /** Fired after a successful delete (already-invalidated queries). */
  onDeleted?: () => void;
  /** Override delete mutation (e.g. season card mock store). */
  onConfirmDelete?: () => Promise<void>;
  /** Extra query keys to invalidate on success. `mainCards` and
   *  `publishedCards` are always invalidated; pass more here if a specific
   *  thumbnail lives in another list. */
  extraInvalidateKeys?: ReadonlyArray<ReadonlyArray<unknown>>;
}

/** Reusable delete-confirmation dialog for any card thumbnail. Owns the
 *  `useDeleteMainCard` mutation and post-delete query invalidation so callers
 *  only manage `open`/`onClose` state and pass the card id. */
export function DeleteCardConfirmDialog({
  open,
  onClose,
  cardId,
  cardTitle,
  onDeleted,
  onConfirmDelete,
  extraInvalidateKeys,
}: DeleteCardConfirmDialogProps) {
  const { darkMode } = useThemeStore();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentProfile } = useCurrentTalentProfile();

  const numericId = Number(cardId);

  const { update: deleteMainCard, isLoading: isDeleting } = useDeleteMainCard({
    key: ["deleteMainCard", cardId],
    params: {
      id: Number.isFinite(numericId) ? numericId : 0,
      ...(currentProfile?.id
        ? { headers: { "talent-id": String(currentProfile.id) } }
        : {}),
    },
  });

  const handleConfirm = async () => {
    const invalidateLists = () => {
      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          typeof q.queryKey[0] === "string" &&
          (q.queryKey[0].startsWith("mainCards") ||
            q.queryKey[0].startsWith("publishedCards")),
      });
      extraInvalidateKeys?.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [...key] });
      });
    };

    if (onConfirmDelete) {
      try {
        await onConfirmDelete();
        invalidateLists();
        enqueueSnackbar(
          t("card_deleted_successfully", {
            ns: "taascard",
            defaultValue: "Card deleted successfully.",
          }),
          { variant: "success" },
        );
        onDeleted?.();
        onClose();
      } catch (err: any) {
        enqueueSnackbar(
          err?.response?.data?.message ||
            err?.message ||
            t("failed_to_delete_card", {
              ns: "taascard",
              defaultValue: "Failed to delete card.",
            }),
          { variant: "error" },
        );
      }
      return;
    }

    try {
      await deleteMainCard(
        {},
        {
          onSuccess: () => {
            invalidateLists();
            enqueueSnackbar(
              t("card_deleted_successfully", {
                ns: "taascard",
                defaultValue: "Card deleted successfully.",
              }),
              { variant: "success" }
            );
            onDeleted?.();
            onClose();
          },
          onError: (err: any) => {
            enqueueSnackbar(
              err?.response?.data?.message ||
                err?.message ||
                t("failed_to_delete_card", {
                  ns: "taascard",
                  defaultValue: "Failed to delete card.",
                }),
              { variant: "error" }
            );
          },
        }
      );
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.message ||
          err?.message ||
          t("failed_to_delete_card", {
            ns: "taascard",
            defaultValue: "Failed to delete card.",
          }),
        { variant: "error" }
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isDeleting) onClose();
      }}
      onClick={(e) => e.stopPropagation()}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          bgcolor: darkMode ? "var(--neutral-900)" : "var(--neutralWhite)",
          borderRadius: 3,
          m: { xs: 2, sm: 3 },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: darkMode ? "var(--neutralWhite)" : "var(--neutral-1000)",
          fontFamily: '"Lexend", sans-serif',
        }}
      >
        {t("delete_card", {
          ns: "taascard",
          defaultValue: "Delete card",
        })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          sx={{
            color: darkMode ? "var(--neutral-300)" : "var(--neutral-600)",
            fontSize: { xs: "0.875rem", sm: "0.95rem" },
            lineHeight: 1.55,
          }}
        >
          {cardTitle
            ? t("delete_card_confirm_named", {
                ns: "taascard",
                title: cardTitle,
                defaultValue: `Are you sure you want to delete "${cardTitle}"? This action cannot be reversed.`,
              })
            : t("delete_card_confirm", {
                ns: "taascard",
                defaultValue:
                  "Are you sure you want to delete this card? This action cannot be reversed.",
              })}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 0.5 }}>
        <Button
          onClick={onClose}
          disabled={isDeleting}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: darkMode ? "var(--neutral-300)" : "var(--neutral-600)",
          }}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isDeleting}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            bgcolor: "#ef4444",
            color: "#fff",
            px: 2,
            "&:hover": { bgcolor: "#dc2626" },
            "&.Mui-disabled": { bgcolor: "#ef4444", opacity: 0.6 },
          }}
        >
          {isDeleting ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />
              {t("deleting", { defaultValue: "Deleting..." })}
            </>
          ) : (
            t("delete", { defaultValue: "Delete" })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteCardConfirmDialog;
